import React, { Fragment } from "react";
import {
  AttributeDataType,
  CustomFieldResourceType
} from "@wingspanhq/wingspan-api-sdk";
import { CustomFieldValues } from "../../../../utils/jsonFormSchema";
import {
  WSElement,
  WSFlexBox,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import orderBy from "lodash/orderBy";
import { queriesCustomFields, queriesPayees } from "@query";
import { useSession } from "@hooks/useSession";
import { WSQueries } from "../../../../query/WSQuery";
import { FieldView } from "../../../../shared/components/FieldView/FieldView";
import { useUpdateCustomFieldsModal } from "./UpdateCustomFieldsModal";
import { useLocalStorage } from "../../../../shared/hooks/useLocalStorage";
import { convertDataTypeBoolean } from "../../../../shared/utils/convertDataTypeBoolean";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";

type CustomFieldsWidgetProps = {
  payeeId: string;
  isInactive: boolean;
};

export const CustomFieldsWidget: React.FC<CustomFieldsWidgetProps> = props => {
  const { personId } = useSession();
  const xWingspanAccount = useXWingspanPayerId();
  const [showEmptyFields, setShowEmptyFields] = useLocalStorage(
    personId + ":empty-custom-fields-expanded",
    true
  );
  const updateCustomFieldsModal = useUpdateCustomFieldsModal();
  const queryCustomFields = queriesCustomFields.listCustomFields.useQueryAll({
    filter: { resourceType: { eq: CustomFieldResourceType.PAYEE } }
  });
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId: props.payeeId,
    xWingspanAccount
  });
  const queryPayeeCustomFields = queriesPayees.getPayeeCustomFields.useQuery({
    payeeId: props.payeeId,
    xWingspanAccount
  });

  const displayRule = (
    rule: string | boolean | null | undefined | string[]
  ) => {
    return showEmptyFields ? true : Boolean(rule);
  };

  return (
    <WSQueries
      renderLoader={() => null}
      renderErrors={() => null}
      queries={{
        queryCustomFields,
        queryPayee,
        queryPayeeCustomFields
      }}
    >
      {({ queryPayeeData, queryPayeeCustomFieldsData }) => {
        const payeeCustomFields = queryCustomFields.flatData;
        // v3 getPayeeCustomFields returns the same keyed value map; cast once
        // at this boundary to the v1 shape the display/util helpers expect.
        const additionalData = (queryPayeeCustomFieldsData ||
          {}) as unknown as CustomFieldValues;
        const externalId = queryPayeeData.externalId ?? undefined;

        return (
          <WSElement>
            <WSSectionToolbar
              mb="S"
              title="Custom fields"
              actions={[
                {
                  label: `Edit`,
                  icon: "edit",
                  onClick: () =>
                    updateCustomFieldsModal.open({
                      payeeId: props.payeeId,
                      externalId,
                      customFields: additionalData,
                      allCustomFields: payeeCustomFields
                    })
                },
                {
                  icon: showEmptyFields ? "hide" : "view",
                  label: `${
                    showEmptyFields ? "Hide" : "Show"
                  } empty custom fields`,
                  onClick: () => setShowEmptyFields(prev => !prev)
                }
              ]}
              button={{
                disabled: props.isInactive,
                label: "Edit",
                onClick: () => {
                  updateCustomFieldsModal.open({
                    payeeId: props.payeeId,
                    externalId,
                    customFields: additionalData,
                    allCustomFields: payeeCustomFields
                  });
                }
              }}
            />
            <WSPanel>
              <WSFlexBox direction="column" gap="M">
                {displayRule(externalId) && (
                  <FieldView label="External ID" value={externalId || "-"} />
                )}
                {orderBy(payeeCustomFields, "dataType", "desc").map(
                  (field, index) => {
                    return (
                      <Fragment key={(field.name ?? "") + index}>
                        {displayRule(additionalData[field.key ?? ""]) ? (
                          <FieldView
                            label={field.name ?? ""}
                            value={
                              field.dataType === AttributeDataType.BOOLEAN
                                ? convertDataTypeBoolean(
                                    additionalData[field.key ?? ""],
                                    "Yes",
                                    "No"
                                  )
                                : additionalData[field.key ?? ""] || "-"
                            }
                          />
                        ) : null}
                      </Fragment>
                    );
                  }
                )}
              </WSFlexBox>
            </WSPanel>
          </WSElement>
        );
      }}
    </WSQueries>
  );
};
